<script setup>
import { ref } from 'vue'
import TermsOfService from './TermsOfService.vue'

defineProps({ isHome: Boolean })
const legalDialog = ref(false)
const tos = ref(null)
</script>

<template><footer class="site-footer" :class="{ 'is-home': isHome }">
    <div class="footer-inner">
      <div class="footer-brand">
        <img class="footer-logo" src="https://cdn.nightriderz.world/images/website/logo/NRZ_Wide.svg" alt="NightRiderz">
        <p class="footer-tag">Need for Speed: World — still racing after dark.</p>
        <div class="footer-social">
          <a href="https://discord.com/invite/VarWbyq" target="_blank" rel="noopener noreferrer" aria-label="Discord"><v-icon>mdi-discord</v-icon></a>
          <a href="https://www.twitch.tv/directory/category/need-for-speed-world" target="_blank" rel="noopener noreferrer" aria-label="Twitch"><v-icon>mdi-twitch</v-icon></a>
          <a href="https://soapboxrace.world/" target="_blank" rel="noopener noreferrer" aria-label="Soapbox Race World" title="Soapbox Race World"><v-icon>mdi-web</v-icon></a>
        </div>
      </div>
      <nav class="footer-nav" aria-label="Footer">
        <div class="footer-col">
          <h3>Play</h3>
          <RouterLink to="/download">Download</RouterLink>
          <RouterLink to="/leaderboards/all/rotation">Leaderboards</RouterLink>
          <RouterLink to="/crews">Crews</RouterLink>
          <RouterLink to="/challenges">Challenges</RouterLink>
        </div>
        <div class="footer-col">
          <h3>Explore</h3>
          <RouterLink to="/drivers">Drivers</RouterLink>
          <RouterLink to="/liveries">Liveries</RouterLink>
          <RouterLink to="/guide">Guides</RouterLink>
        </div>
        <div class="footer-col">
          <h3>Community</h3>
          <a href="https://discord.com/invite/VarWbyq" target="_blank" rel="noopener noreferrer">Discord</a>
          <a href="https://nightcrafterz.net/" target="_blank" rel="noopener noreferrer">Minecraft</a>
          <RouterLink to="/additional">New Content</RouterLink>
          <button class="footer-link-btn" type="button" @click="legalDialog = true">Legal Notices</button>
        </div>
      </nav>
    </div>
    <div class="footer-bottom">
      <p class="footer-copy">© NightRiderz {{ new Date().getFullYear() }}. All rights reserved.</p>
      <p class="footer-disclaimer">Not affiliated with Electronic Arts or the Need for Speed™ brand. Need for Speed is a trademark of Electronic Arts.</p>
    </div>
  </footer>

  <v-dialog v-model="legalDialog" max-width="720" scrollable>
    <v-card class="legal-card">
      <div class="legal-header-bg">
        <div class="legal-header">
          <div class="legal-title">Legal Notices</div>
          <v-btn icon variant="text" class="close-btn" @click="legalDialog = false"><v-icon>mdi-close</v-icon></v-btn>
        </div>
      </div>
      <v-card-text class="legal-content">
        <div class="settings-tab-page">
          <p class="dialog-lead">In accordance with Article 6 of French Law No. 2004-575 of June 21, 2004, users are informed of the parties involved in creating and monitoring this site.</p>
          <div class="section-list">
            <div class="section-row">
              <div class="section-body legal-row">
                <div class="legal-icon"><v-icon size="18">mdi-account-tie</v-icon></div>
                <div>
                  <strong>Site publisher</strong>
                  <p>Steven Milleville · Micro-entrepreneur · SIREN 944240530<br>63B rue Marceau, 62790 Leforest, France<br>contact@nightriderz.world · VAT not applicable (CGI Art. 293 B)</p>
                </div>
              </div>
            </div>
            <div class="section-row">
              <div class="section-body legal-row">
                <div class="legal-icon"><v-icon size="18">mdi-server</v-icon></div>
                <div>
                  <strong>Website hosting</strong>
                  <p>Hetzner Online GmbH · Industriestr. 25, 91710 Gunzenhausen, Germany<br>+49 (0)9831 505-0</p>
                </div>
              </div>
            </div>
            <div class="section-row">
              <div class="section-body legal-row">
                <div class="legal-icon"><v-icon size="18">mdi-gavel</v-icon></div>
                <div>
                  <strong>Terms of use</strong>
                  <p>Using the site means you accept the general conditions of use, which may change at any time.</p>
                  <v-btn class="mt-2" size="small" variant="tonal" @click="tos.tosDialog = true"> View Terms of Service </v-btn>
                </div>
              </div>
            </div>
            <div class="section-row">
              <div class="section-body legal-row">
                <div class="legal-icon"><v-icon size="18">mdi-shield-account</v-icon></div>
                <div>
                  <strong>Personal data</strong>
                  <p>No personal information is collected without your knowledge. Data sent via forms or purchases is not resold or used commercially without consent.</p>
                </div>
              </div>
            </div>
            <div class="section-row">
              <div class="section-body legal-row">
                <div class="legal-icon"><v-icon size="18">mdi-copyright</v-icon></div>
                <div>
                  <strong>Intellectual property</strong>
                  <p>Site content (texts, images, code) belongs to its author and may not be copied without authorization.</p>
                </div>
              </div>
            </div>
            <div class="section-row disclaimer">
              <div class="section-body legal-row">
                <div class="legal-icon warning"><v-icon size="18">mdi-alert-circle</v-icon></div>
                <div>
                  <strong>Disclaimer</strong>
                  <p>This project is not affiliated with Electronic Arts. <em>Need for Speed World</em> is a registered EA trademark. NightRiderz is an independent community service.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
  <TermsOfService ref="tos" /></template>

<style scoped>
.site-footer {
  margin-top:auto;
  padding:48px 24px 28px;
  min-height:320px;
  contain:layout style;
  background:var(--primary-color);
  border-top:1px solid var(--line);
  color:var(--ink-soft)}
.site-footer.is-home {
  background:var(--primary-color)}
.footer-inner {
  width:min(1120px,100%);
  margin:0 auto;
  display:grid;
  grid-template-columns:minmax(0,1.2fr) minmax(0,1.6fr);
  gap:40px;
  align-items:start}
.footer-brand {
  display:flex;
  flex-direction:column;
  gap:14px;
  align-items:flex-start}
.footer-logo {
  display:block;
  height:28px;
  width:auto}
.footer-tag {
  margin:0;
  max-width:320px;
  color:var(--mute);
  font-size:.9rem;
  line-height:1.45}
.footer-social {
  display:flex;
  gap:8px;
  margin-top:4px}
.footer-social a {
  display:inline-flex;
  align-items:center;
  justify-content:center;
  width:36px;
  height:36px;
  border-radius:10px;
  border:1px solid var(--line-strong);
  background:var(--hover-color);
  color:var(--ink)!important;
  text-decoration:none;
  transition:background .15s ease,border-color .15s ease}
.footer-social a:hover {
  background:var(--active-color);
  border-color:var(--line-strong)}
.footer-nav {
  display:grid;
  grid-template-columns:repeat(3,minmax(0,1fr));
  gap:24px}
.footer-col {
  display:flex;
  flex-direction:column;
  align-items:flex-start;
  gap:10px}
.footer-col h3 {
  margin:0 0 2px;
  font-size:.72rem;
  font-weight:800;
  letter-spacing:.12em;
  text-transform:uppercase;
  color:var(--mute)}
.footer-col a, .footer-link-btn {
  margin:0;
  padding:0;
  border:none;
  background:none;
  color:var(--ink-soft)!important;
  font-size:.9rem;
  font-weight:600;
  line-height:1.3;
  text-align:left;
  text-decoration:none;
  cursor:pointer;
  transition:color .15s ease}
.footer-col a:hover, .footer-link-btn:hover {
  color:var(--ink)!important}
.footer-bottom {
  width:min(1120px,100%);
  margin:36px auto 0;
  padding-top:20px;
  border-top:1px solid var(--line);
  display:flex;
  flex-direction:column;
  gap:10px}
.footer-copy {
  margin:0;
  font-size:.82rem;
  font-weight:600;
  color:var(--ink-soft)}
.footer-disclaimer {
  margin:0;
  max-width:720px;
  font-size:.78rem;
  line-height:1.45;
  color:var(--mute)}
.legal-header {
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:12px}
.legal-title {
  font-size:1.05rem;
  font-weight:800;
  letter-spacing:.02em;
  text-transform:uppercase;
  color:var(--ink)}
.legal-row {
  display:flex;
  gap:12px;
  align-items:flex-start}
.legal-icon {
  width:34px;
  height:34px;
  border-radius:9px;
  background:var(--glass-strong);
  display:flex;
  align-items:center;
  justify-content:center;
  flex-shrink:0}
.legal-icon.warning {
  background:#eab3082e}
.legal-row strong {
  display:block;
  margin-bottom:4px;
  color:var(--ink);
  font-size:.92rem}
.legal-row p {
  margin:0;
  color:var(--mute);
  font-size:.82rem;
  line-height:1.5}
.section-row.disclaimer {
  border-color:#eab30847!important}
@media (max-width: 900px) {
.footer-inner {
  grid-template-columns:1fr;
  gap:32px}
.footer-nav {
  grid-template-columns:repeat(3,minmax(0,1fr))}

}
@media (max-width: 640px) {
.site-footer {
  padding:40px 16px 24px}
.footer-nav {
  grid-template-columns:1fr 1fr;
  gap:28px 20px}
.footer-col:last-child {
  grid-column:1 / -1}

}
</style>
